/**
 * The explore radius, from the app's side: which radius is the default, when
 * the app asks the API to widen it, and what it offers when it must not.
 *
 * The tiers themselves are the API's (a-default-search-widens-until-it-finds-
 * enough.ts): the app sends a flag and reads back the radius the search
 * actually covered. They are repeated here only to say which tier the "widen"
 * button goes to, and a spec holds the two lists equal.
 *
 * WHY THE DEFAULT IS 10 KM AND NOT 100
 *
 * Until 15 October 2026 the explore search ran at 100 km for nearly everyone:
 * the state said 10 km, but LocationAccessService and the explore page both
 * overwrote it with 100 before the list loaded (#89). The widening tiers
 * Maude approved that day start at 10 km, so the default came down to 10 km
 * with them (her decision, same day). A sparse area is now handled by
 * widening, not by a wide radius for everybody.
 */
export const DEFAULT_EXPLORE_RADIUS_KM = 10;

export const WIDENING_TIERS_KM: readonly number[] = [10, 20, 30, 50];

export const WIDENING_CAP_KM = WIDENING_TIERS_KM[WIDENING_TIERS_KM.length - 1];

export const ENOUGH_ACTIVITIES = 3;

/**
 * Whether the search may widen this radius on the person's behalf.
 *
 * Only the default, and only when nobody chose it. Both halves matter: a
 * radius restored from an earlier hand-picked city arrives without the
 * "chosen" mark, and the value alone (25 km, say) is still enough to tell it
 * was not the default. Widening it would start from 10 km and could answer a
 * smaller circle than the one they asked for.
 */
export const theSearchMayWiden = (radiusMeter: number | undefined, chosenByPerson: boolean): boolean =>
  !chosenByPerson && radiusMeter === DEFAULT_EXPLORE_RADIUS_KM * 1000;

/**
 * The radius the "widen" button offers for a radius the person chose, or
 * undefined when there is no button.
 *
 * No button when there are already enough results, when the radius is the
 * widening one (the search did it already), or when it is at or past the cap:
 * past 50 km the answer is the alert, not a wider circle.
 */
export const theWiderRadiusToOfferKm = (input: {
  radiusMeter: number | undefined;
  chosenByPerson: boolean;
  resultsCount: number;
}): number | undefined => {
  const { radiusMeter, chosenByPerson, resultsCount } = input;
  if (!chosenByPerson || radiusMeter == null || resultsCount >= ENOUGH_ACTIVITIES) {
    return undefined;
  }
  const radiusKm = radiusMeter / 1000;
  return WIDENING_TIERS_KM.find((tier) => tier > radiusKm);
};

/**
 * The radius the screen says it searched: the one the API answers it covered,
 * or the one sent when the API predates the field. An API deployed before the
 * widening never widens, so the radius sent is then the truth.
 */
export const theRadiusSearchedMeter = (
  answeredMeter: number | undefined,
  sentMeter: number | undefined,
): number | undefined => (Number.isFinite(answeredMeter) ? answeredMeter : sentMeter);

/**
 * The radius every screen names next to the place: the one the last search
 * covered, or the one asked for before any search.
 *
 * One rule for every header, because two had drifted apart. On 23 October
 * 2026 (recette 2.2.11, Asana 1219817527392832) Explorer said « Annecy -
 * 50km » after a default 10 km search widened to 50, and the search page,
 * opened right after, said « Annecy, France - 10km » for the same place: it
 * read the radius asked for, while Explorer had read the one covered since
 * #841. The person could not tell which one applied.
 */
export const theRadiusCoveredMeter = (state: { searchedRadiusMeter?: number; radius: number }): number =>
  state.searchedRadiusMeter ?? state.radius;

/**
 * What the explore screen says about its radius, above the activities.
 *
 * - `widened`: the default search reached past 10 km, and the screen says how
 *   far, so a 30 km badge on a card is not a surprise. Not said when nothing
 *   was found at all: the empty state and its alert already say it, and
 *   "here is what we found up to 50 km" over an empty list would be untrue.
 * - `offer-wider`: the person chose the radius and it holds fewer than three,
 *   so the screen offers the next tier instead of widening behind their back.
 */
export type ExploreRadiusNotice =
  | { kind: 'widened'; fromKm: number; toKm: number }
  | { kind: 'offer-wider'; currentKm: number; toKm: number };

export const theRadiusNotice = (input: {
  widened: boolean;
  searchedRadiusMeter: number | undefined;
  radiusMeter: number | undefined;
  chosenByPerson: boolean;
  resultsCount: number;
}): ExploreRadiusNotice | undefined => {
  if (input.widened && input.searchedRadiusMeter != null && input.resultsCount > 0) {
    return { kind: 'widened', fromKm: DEFAULT_EXPLORE_RADIUS_KM, toKm: input.searchedRadiusMeter / 1000 };
  }
  const toKm = theWiderRadiusToOfferKm(input);
  if (toKm != null && input.radiusMeter != null) {
    return { kind: 'offer-wider', currentKm: input.radiusMeter / 1000, toKm };
  }
  return undefined;
};
